import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpOrderService from "@plannel/services/fp/fp-order-service";
import DemandPopup from "@plannel/pages/factory-plan/common/popup/DemandPopup";
import OrderTypePopup from "@plannel/pages/factory-plan/common/popup/OrderTypePopup";
import InventoryPopup from "@plannel/pages/factory-plan/common/popup/InventoryPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import ColorPickerRenderer from "@plannel/pages/factory-plan/common/component/ColorPickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 3개(woCd/inventoryCd/dueDate) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "woCd", label: "FP_WO_CD" },
  { field: "inventoryCd", label: "FP_INVENTORY_CD" },
  { field: "dueDate", label: "FP_DUE_DT" }
];

// TODO(MIG-T2): [order/WorkOrderTab] 고객 담당자(salesPerson)/고객 납기일(customerDueDate) 미이관.
//   old: sales 그룹 하위에 salesPerson(읽기전용)/customerDueDate(읽기전용) 컬럼 존재.
//   사유: 이관된 backend WorkOrderDto에 대응 필드 자체가 없음(DemandTab과 동일 사유).

/**
 * 작업주문(Work Order) 탭 — old WorkOrderTab.jsx 대응. §11-1(항상 마운트 + display 토글)로
 * Order.js에서 렌더링되며, planScope prop 변경 시에만 자체 재조회한다(§11-3).
 *
 * props: t, viewName, planScope, searchFields(inventoryCdNm/dmndCdNm/woCdNm)
 */
const WorkOrderTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [workOrder, setWorkOrder] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [inventoryPopup, setInventoryPopup] = useState({ open: false, node: null });
  const [demandPopup, setDemandPopup] = useState({ open: false, node: null });
  const [orderTypePopup, setOrderTypePopup] = useState({ open: false, node: null, editable: false });

  const planStrategyValuesRef = useCommonCodeValues("FP_PLAN_STRATEGY");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_WORK_ORDER"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // planScope는 서버 조회, inventoryCdNm/dmndCdNm/woCdNm은 AG Grid 외부 필터로 즉시 필터(§6-1).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { inventoryCdNm, dmndCdNm, woCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (inventoryCdNm && !(hit(r.inventoryCd, inventoryCdNm) || hit(r.inventoryNm, inventoryCdNm))) return false;
    if (dmndCdNm && !hit(r.dmndCd, dmndCdNm)) return false;
    if (woCdNm && !hit(r.woCd, woCdNm)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  // old inventoryStyleCallback — dmndCd가 이미 있으면(연결된 판매주문에서 인벤토리 결정됨)
  // inventoryCd는 편집/팝업 모두 잠금.
  const isInventoryLocked = (data) => !!data?.dmndCd;

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_SO_CD", field: "dmndCd", colId: "dmndCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setDemandPopup({ open: true, node }) } },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_WO_CD", field: "woCd", colId: "woCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_INVENTORY_CD", field: "inventoryCd", colId: "inventoryCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => { if (!isInventoryLocked(node.data)) setInventoryPopup({ open: true, node }); } } },
    { headerName: "FP_INVENTORY_NAME", field: "inventoryNm", colId: "inventoryNm", type: ["nonEditableColumn"], width: 220 },
    { headerName: "FP_REQUEST_QTY", field: "requestQty", colId: "requestQty", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_CUSTOMER_CD", field: "customerCd", colId: "customerCd", type: ["nonEditableColumn"], width: 180 },
    { headerName: "FP_CUSTOMER_NM", field: "customerNm", colId: "customerNm", type: ["nonEditableColumn"], width: 220 },
    { headerName: "FP_DUE_DT", field: "dueDate", colId: "dueDate", width: 160, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_PRIORITY", field: "priority", colId: "priority", width: 100, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110 },
    { headerName: "FP_DISPLAY_COLOR", field: "displayColor", colId: "displayColor", width: 100,
      editable: false, cellRenderer: ColorPickerRenderer, cellStyle: { padding: 0 } },
    { headerName: "FP_DUE_DT_FENCE", field: "dueDateFence", colId: "dueDateFence", width: 160, type: ["dateTimeColumn", "centerAligned"] },
    {
      headerName: "FP_PLAN_POLICY", groupId: "planPolicy", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_ORDER_STRATEGY", field: "orderStrategyTpCd", colId: "orderStrategyTpCd", width: 110, type: ["centerAligned"],
          cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
          cellEditorParams: () => codeComboCellEditorParams("FP_PLAN_STRATEGY", planStrategyValuesRef, t) },
        { headerName: "FP_PST", field: "pst", colId: "pst", width: 160, type: ["dateTimeColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "FP_EFFICIENCY", field: "efficiency", colId: "efficiency", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" },
        { headerName: "FP_TO_STOCK_YN", field: "toStockYn", colId: "toStockYn", width: 100, type: ["booleanColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "FP_CANC_ON_LATE_YN", field: "cancOnLateYn", colId: "cancOnLateYn", width: 100, type: ["booleanColumn", "centerAligned"], columnGroupShow: "open" },
        { headerName: "FP_ORDER_TP_CD", field: "orderTpCd", colId: "orderTpCd", cellClass: "stringType", width: 130, columnGroupShow: "open",
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setOrderTypePopup({ open: true, node, editable: false }) } }
      ]
    },
    {
      headerName: "FP_EXTRA_INFO", groupId: "extraInfo", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "FP_ACTIVE_YN", field: "activeYn", colId: "activeYn", width: 100, type: ["booleanColumn", "centerAligned"] },
        { headerName: "FP_GRP_PRIORITY", field: "grpPriority", colId: "grpPriority", width: 100, type: ["numberColumn", "rightAligned"], columnGroupShow: "open" }
      ]
    },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveWorkOrder = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpOrderService.getWorkOrders({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setWorkOrder([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setWorkOrder(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          gridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveWorkOrder();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, dmndCd: "", woCd: "", inventoryCd: "", inventoryNm: "", requestQty: 0,
      customerCd: "", customerNm: "", dueDate: null, priority: 1, descTxt: "",
      displayColor: "#ff96c8", dueDateFence: null, orderStrategyTpCd: "FORWARD", pst: null,
      efficiency: 1, toStockYn: false, cancOnLateYn: false, orderTpCd: "", activeYn: true, grpPriority: 1
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeWorkOrder = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpOrderService.removeWorkOrders(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveWorkOrder());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveWorkOrder = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpOrderService.upsertWorkOrders(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveWorkOrder());
  };

  const onSelectInventory = (row) => {
    const node = inventoryPopup.node;
    if (node) {
      node.setDataValue("inventoryCd", row.inventoryCd);
      node.setDataValue("inventoryNm", row.inventoryNm);
    }
  };

  // old setDemandValues — 판매주문을 선택하면 해당 주문의 인벤토리/고객/납기 정보까지 함께 반영.
  const onSelectDemand = (row) => {
    const node = demandPopup.node;
    if (node) {
      node.setDataValue("dmndCd", row.dmndCd);
      node.setDataValue("inventoryCd", row.inventoryCd);
      node.setDataValue("inventoryNm", row.inventoryNm);
      node.setDataValue("customerCd", row.customerCd);
      node.setDataValue("dueDate", row.dueDate);
      node.setDataValue("dueDateFence", row.dueDateFence);
    }
  };

  // old setOrderTypeValues — orderTpCd 선택 시 정책값(계획방식/효율/생산량재고화/납기지연취소)까지 반영.
  const onSelectOrderType = (row) => {
    const node = orderTypePopup.node;
    if (node) {
      node.setDataValue("orderTpCd", row.orderTpCd);
      node.setDataValue("orderStrategyTpCd", row.orderStrategyTpCd);
      node.setDataValue("pst", row.orderPst);
      node.setDataValue("efficiency", row.orderEfficiency);
      node.setDataValue("toStockYn", row.toStockYn);
      node.setDataValue("cancOnLateYn", row.cancOnLateYn);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveWorkOrder();
    else if (action === "DELETE") removeWorkOrder();
  };

  // 화면 내부 탭(§11-3): planScope가 바뀔 때만 재조회.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveWorkOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex" }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_WORK_ORDER")}.xlsx`, sheetName: t("FP_WORK_ORDER") })} />
          <Button size="small" onClick={() => setOrderTypePopup({ open: true, node: null, editable: true })}>
            {t("FP_ORDER_TYPE_MST")}
          </Button>
        </Box>
        <Box sx={{ display: "flex" }}>
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </Box>
      <Box id="fp-workorder-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={workOrder}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <InventoryPopup
        open={inventoryPopup.open}
        onClose={() => setInventoryPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectInventory}
      />
      <DemandPopup
        open={demandPopup.open}
        onClose={() => setDemandPopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectDemand}
      />
      <OrderTypePopup
        open={orderTypePopup.open}
        onClose={() => setOrderTypePopup({ open: false, node: null, editable: false })}
        planScope={planScope}
        onSelect={onSelectOrderType}
        editable={orderTypePopup.editable}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(WorkOrderTab);
